import { Injectable } from '@angular/core';

/**
 * Gestisce la destinazione post-login (returnUrl) proveniente dai link email.
 *
 * L'URL viene salvato in sessionStorage al momento del 401 (nei guard) e
 * consumato una sola volta all'atterraggio in dashboard. Cosi' sopravvive a
 * qualsiasi deviazione intermedia (login, doubleoptin, 428, ecc.) senza dover
 * transitare per la URL o la query string.
 */
@Injectable({
    providedIn: 'root'
})
export class RedirectService {

    private readonly KEY = 'postLoginReturnUrl';

    private readonly pazienteRoutes = ['/consultopaziente', '/preconsultopaziente', '/paziente'];
    private readonly medicoRoutes = ['/consulto', '/medico', '/agenda'];

    /**
     * Salva la destinazione post-login (sanitizzata). Se non valida, pulisce.
     */
    save(url: string | null): void {
        const sanitized = this.sanitize(url);
        if (sanitized) {
            sessionStorage.setItem(this.KEY, sanitized);
        } else {
            this.clear();
        }
    }

    /**
     * Legge e rimuove la destinazione salvata (ri-sanitizzata per sicurezza).
     */
    consume(): string | null {
        const url = this.sanitize(sessionStorage.getItem(this.KEY));
        this.clear();
        return url;
    }

    /**
     * Legge la destinazione salvata SENZA rimuoverla.
     */
    peek(): string | null {
        return this.sanitize(sessionStorage.getItem(this.KEY));
    }

    clear(): void {
        sessionStorage.removeItem(this.KEY);
    }

    /**
     * Sanitizza il returnUrl per prevenire open redirect attacks.
     */
    sanitize(url: string | null): string | null {
        if (!url) {
            return null;
        }

        let decoded: string;
        try {
            decoded = decodeURIComponent(url);
        } catch {
            return null;
        }

        // Deve essere un path relativo (/ singolo)
        if (!decoded.startsWith('/') || decoded.startsWith('//')) {
            return null;
        }

        const lowerDecoded = decoded.toLowerCase();
        if (lowerDecoded.includes('javascript:') || lowerDecoded.includes('data:') ||
            lowerDecoded.includes('vbscript:') || lowerDecoded.includes('http:') ||
            lowerDecoded.includes('https:')) {
            return null;
        }

        // Rimuove eventuali caratteri di controllo
        return decoded.replace(/[\x00-\x1f\x7f]/g, '');
    }

    /**
     * Verifica che il returnUrl sia compatibile con il ruolo dell'utente.
     */
    isValidForRole(ruolo: string, url: string): boolean {
        if (ruolo === 'paziente') {
            return this.pazienteRoutes.some(route => url.startsWith(route));
        }
        if (ruolo === 'medico') {
            return this.medicoRoutes.some(route => url.startsWith(route));
        }
        // Per altri ruoli (admin, operatore) non si usa il returnUrl
        return false;
    }
}
